import React, { useContext, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import mediaData from '../../data/media/media-content.json';
import './MediaCenter.css';

/* How many video cards to show per batch */
const INITIAL_BATCH = 4;

export default function MediaCenter() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand?.brandKey || 'gso';
  const brandName = brand?.name || 'Our Practice';
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';

  const filteredVideos = useMemo(() => (mediaData.youtube || []).filter(
    v => v?.brand === 'all' || v?.brand === brandKey
  ), [brandKey]);

  const filteredNews = (mediaData.news || []).filter(
    a => a?.brand === 'all' || a?.brand === brandKey
  );

  /* Lazy-load batching */
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const visibleVideos = filteredVideos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredVideos.length;

  const canonicalUrl = `https://${baseDomain}/media`;
  const pageTitle = t('media.pageTitle', locale).replace('{{brandName}}', brandName);
  const pageDesc = t('media.metaDesc', locale).replace('{{brandName}}', brandName);
  const ogImage = `https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description: pageDesc,
    url: canonicalUrl,
    video: filteredVideos.map(v => ({
      '@type': 'VideoObject',
      name: v.title?.[locale] || v.title?.en || '',
      embedUrl: `https://www.youtube-nocookie.com/embed/${v.id}`,
      thumbnailUrl: `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`,
      uploadDate: v.uploadDate || '2024-01-01',
    })),
  };

  return (
    <div className="media-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/media" />

      {/* Hero */}
      <section className="media-hero glass-dark">
        <div className="container">
          <h1>{t('media.heroTitle', locale)}</h1>
          <p>{t('media.heroSubtitle', locale).replace('{{brandName}}', brandName)}</p>
        </div>
      </section>

      {/* ViewMedica — all brands */}
      {mediaData.viewmedica?.embedUrl && (
        <section className="section-padding">
          <div className="container" style={{ maxWidth: 900 }}>
            <h2 className="section-title">{t('media.patientEd', locale)}</h2>
            <p className="section-subtitle">{t('media.patientEdDesc', locale)}</p>
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden', borderRadius: 16, marginBottom: 40 }}>
              <div className="video-embed">
                <iframe
                  src={mediaData.viewmedica?.embedUrl}
                  title={t('media.patientEd', locale)}
                  frameBorder="0"
                  allowFullScreen
                  loading="lazy"
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* YouTube Video Playlist — lazy-loaded in batches */}
      <section className="section-padding">
        <div className="container">
          <h2 className="section-title">{t('media.videoPlaylist', locale)}</h2>
          <p className="section-subtitle">{t('media.videoPlaylistDesc', locale)}</p>
          <div className="video-grid">
            {visibleVideos.map((video) => (
              <div key={video.id} className="video-card glass-card">
                <div className="video-embed">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                    title={video.title?.[locale] || video.title?.en || ''}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="video-info">
                  <h3>{video.title?.[locale] || video.title?.en || ''}</h3>
                </div>
              </div>
            ))}
          </div>
          {hasMore && (
            <div className="load-more-wrap">
              <button
                className="load-more-btn"
                onClick={() => setVisibleCount(prev => prev + INITIAL_BATCH)}
              >
                {t('media.loadMore', locale) || 'Load More Videos'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* News & Updates */}
      {filteredNews.length > 0 && (
        <section className="section-padding bg-alt">
          <div className="container">
            <h2 className="section-title">{t('media.news', locale)}</h2>
            <div className="news-grid">
              {filteredNews.map((article, i) => (
                <Link
                  key={i}
                  to={localizedPath(`/blog/${article.slug}`, locale)}
                  className="news-card glass-card"
                >
                  <h3>{article.title?.[locale] || article.title?.en || ''}</h3>
                  <span className="news-link">{t('media.readArticle', locale)} →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
